import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { COMPANY_COLORS } from "@/components/AgentDashboard";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell } from "recharts";
import { t } from "@/lib/i18n";

const fmt = (d) => d.toISOString().slice(0, 10);
const PRESETS = {
  "30d": () => { const s = new Date(); s.setDate(s.getDate() - 29); return { start: fmt(s), end: fmt(new Date()) }; },
  year: () => ({ start: `${new Date().getFullYear()}-01-01`, end: fmt(new Date()) }),
  prev: () => { const y = new Date().getFullYear() - 1; return { start: `${y}-01-01`, end: `${y}-12-31` }; },
  all: () => ({ start: "", end: "" }),
};
const PRESET_LABELS = { "30d": "30 giorni", year: "Anno in corso", prev: "Anno scorso", all: "Sempre", custom: "Personalizzato" };

function Filters({ f, setF, companies, customers }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="agent-stats-filters">
      <div className="space-y-1">
        <Label className="text-xs">{t("Azienda")}</Label>
        <Select value={f.company} onValueChange={(v) => setF({ ...f, company: v, customer: "all" })}>
          <SelectTrigger data-testid="agent-stats-company-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Tutte le aziende")}</SelectItem>
            {companies.map((c) => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("Cliente")}</Label>
        <Select value={f.customer} onValueChange={(v) => setF({ ...f, customer: v })}>
          <SelectTrigger data-testid="agent-stats-customer-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("Tutti i clienti")}</SelectItem>
            {customers.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}{c.companies.length > 1 ? ` · ${c.companies.length} aziende` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("Periodo")}</Label>
        <Select value={f.preset} onValueChange={(v) => setF({ ...f, preset: v })}>
          <SelectTrigger data-testid="agent-stats-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PRESET_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {f.preset === "custom" && (
        <div className="flex gap-2 items-end">
          <Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="agent-stats-start" />
          <Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="agent-stats-end" />
        </div>
      )}
    </div>
  );
}

function Kpis({ s }) {
  const items = [[t("Fatturato"), eur(s?.total_sales)], [t("Ordini"), s?.orders_count ?? 0], [t("Ordine medio"), eur(s?.avg_order)], [t("Provvigioni"), eur(s?.total_commission)]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map(([l, v]) => (
        <div key={l} className="bg-card border border-border rounded-2xl p-5" data-testid={`agent-stat-${l.toLowerCase().replace(/\s/g, "-")}`}>
          <span className="text-xs font-mono-j uppercase tracking-wider text-muted-foreground">{l}</span>
          <p className="font-display text-2xl font-extrabold mt-3">{v}</p>
        </div>
      ))}
    </div>
  );
}

function CompanyTable({ rows, colorOf }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <h3 className="font-semibold mb-4">{t("Fatturato portato per azienda")}</h3>
      <div className="h-[220px] mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="company_name" stroke="#64748b" fontSize={11} />
            <YAxis stroke="#64748b" fontSize={11} width={56} />
            <Tooltip formatter={(v) => [eur(v), "Fatturato"]} />
            <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>{rows.map((r) => <Cell key={r.company_id} fill={colorOf(r.company_id)} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <table className="w-full text-sm" data-testid="agent-stats-company-table">
        <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-2">{t("Azienda")}</th><th className="text-right">{t("Ordini")}</th><th className="text-right">{t("Fatturato")}</th><th className="text-right">{t("Provvigione")}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.company_id} className="border-t border-border" data-testid={`agent-stats-row-${r.company_id}`}>
              <td className="py-2 flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: colorOf(r.company_id) }} />{r.company_name}</td>
              <td className="text-right">{r.orders}</td>
              <td className="text-right font-semibold">{eur(r.revenue)}</td>
              <td className="text-right text-muted-foreground">{eur(r.commission)} ({r.commission_rate}%)</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AgentStatistics() {
  const { companies } = useAuth();
  const [f, setF] = useState({ company: "all", customer: "all", preset: "year", ...PRESETS.year() });
  const range = f.preset === "custom" ? { start: f.start, end: f.end } : PRESETS[f.preset]();
  const params = { company_id: f.company === "all" ? undefined : f.company, customer: f.customer === "all" ? undefined : f.customer, start: range.start || undefined, end: range.end || undefined };
  const { data: s } = useQuery({ queryKey: ["agent-stats", params], queryFn: async () => (await api.get("/agent/stats", { params })).data });
  const colorOf = (id) => COMPANY_COLORS[Math.max(0, companies.findIndex((c) => c.company_id === id)) % COMPANY_COLORS.length];

  return (
    <div className="space-y-6" data-testid="agent-statistics">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Statistiche")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{range.start || t("inizio")} → {range.end || t("oggi")} {t("· solo ordini confermati ed evasi")}</p>
      </div>
      <Filters f={f} setF={setF} companies={companies} customers={s?.customers || []} />
      <Kpis s={s} />
      <div className="grid lg:grid-cols-2 gap-6">
        <CompanyTable rows={s?.by_company || []} colorOf={colorOf} />
        <div className="bg-card border border-border rounded-2xl p-5">
          <h3 className="font-semibold mb-4">{t("Andamento fatturato")}</h3>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={s?.revenue_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#64748b" fontSize={11} width={56} />
                <Tooltip formatter={(v) => [eur(v), "Fatturato"]} />
                <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <h3 className="font-semibold mt-6 mb-2">{t("Prodotti più venduti")}</h3>
          <div className="space-y-1" data-testid="agent-stats-top-products">
            {(s?.top_products || []).slice(0, 6).map((p) => (
              <div key={`${p.company_name}-${p.name}`} className="flex justify-between text-sm border-b border-border/60 py-1.5">
                <span className="truncate">{p.name} <span className="text-xs text-muted-foreground">· {p.company_name}</span></span>
                <span className="font-semibold">{eur(p.revenue)}</span>
              </div>
            ))}
            {!s?.top_products?.length && <p className="text-sm text-muted-foreground">{t("Nessun dato nel periodo.")}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
